import { Outlet, useLocation, Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { ErrorState } from '@/components/feedback/ErrorState'
import { useSession } from '@/features/auth/api/useSession'
import { ApiError } from '@/lib/api'

/* The gate in front of every authenticated route.
 *
 * This is NOT access control. The API is the only real gate (AD-21) and it
 * serves no data a user may not see regardless of what renders here. What this
 * does is spare a signed-out user a screen full of failed requests and send
 * them somewhere they can act — a redirect, not a permission check.
 *
 * A session that has run out its idle (8h) or absolute (7d) clock arrives here
 * the same way a never-signed-in visitor does: as a 401. The two are told apart
 * only by whether the query has ever succeeded, which is what `expired` below
 * carries to the sign-in page so it can say why the user was interrupted
 * instead of appearing to have forgotten them.
 */

export function RequireSession() {
  const { t } = useTranslation()
  const location = useLocation()
  const { data: user, isPending, isError, error, isSuccess, refetch } = useSession()

  /* Hold the frame while we find out. Rendering the sign-in page during this
   * window would flash a form at a user who IS signed in, on every cold load —
   * the session query is a round trip, and its answer is usually "yes". */
  if (isPending) {
    return (
      <div
        className="bg-surface-page flex min-h-dvh items-center justify-center"
        aria-busy="true"
        aria-live="polite"
        aria-label={t('auth.signIn.pending')}
      />
    )
  }

  /* A 401 is the expected answer for a signed-out user, not a failure worth
   * reporting. Anything else — the server is down, the network dropped — is a
   * real error, and bouncing to sign-in would be a lie: the credentials are not
   * the problem and re-entering them will not help. */
  if (isError) {
    const unauthenticated = error instanceof ApiError && error.isUnauthenticated
    if (!unauthenticated) {
      return (
        <div className="bg-surface-page flex min-h-dvh items-center justify-center p-4">
          <ErrorState error={error} onRetry={refetch} className="max-w-[420px]" />
        </div>
      )
    }
  }

  if (!user) {
    return (
      <Navigate
        to="/login"
        replace
        state={{
          /* Where they were headed, so sign-in returns them there rather than
             dropping everyone on the dashboard. Search and hash included: a
             filtered list or a case section is exactly the kind of link that
             gets shared and then demands a sign-in. */
          from: `${location.pathname}${location.search}${location.hash}`,
          /* The query succeeded before, so there WAS a session and it ended.
             Only then is "your session ended" true — saying it to a first-time
             visitor would invent a session they never had. */
          expired: isSuccess,
        }}
      />
    )
  }

  return <Outlet />
}
